import { createTheme, darken } from "@mui/material/styles";

export const ORANGE = "#ff914d";
export const GREEN = "#3b9097";
export const BLUE = "#0cc0df";
export const DARK = "#443F53";
export const BLUE_DARK = "#1C2D40";
export const CREAM = "#f5f1e5";
export const RED = "#db5d6e";
export const PURPLE = "#8c52ff";
export const DARK_BLUE = "#0288d1";
export const GRAY_VISUAL = "#EEECEA";
export const GRAY_BG = "#C1BABD";

/**
 * Backdrop for the service icon discs on the landing page.
 *
 * A tint rather than a palette entry, following `GRAY_VISUAL`: one component consumes it, and the
 * theme has no `surface.*` group to put it in yet. If a second surface tint ever appears, this is
 * the pair to promote.
 */
export const BLUE_PALE = "#e3eef3";

/**
 * Every piece of text the theme calls secondary (EB-554).
 *
 * It used to be `BLUE_LIGHT` (`#c2cfd2`), which contrasts **1.5:1** against white — a quarter of
 * what WCAG AA asks of body text, and visibly so: the "A cotizar" explanation and the resting
 * labels of every `TextField` (MUI draws `InputLabel` in `text.secondary`) read as barely-there
 * grey. This is desaturated `BLUE_DARK` rather than a neutral grey, so secondary text still belongs
 * to the same family as primary; it clears 4.5:1 on white *and* on `CREAM`, which is what the
 * cards it most often sits on are painted with.
 */
export const TEXT_SECONDARY = "#55677A";

/** Enough fade for a placeholder to read as a hint, not so much that it stops being readable. */
const PLACEHOLDER_OPACITY = 0.72;

/**
 * The colours of the act of buying (EB-536), sampled from the reference image the business
 * supplied — see `docs/product-detail-redesign/README.md` §5.
 *
 * Grouped as `purchase` rather than `cta` on purpose: these belong to the two buttons that take
 * money, not to any call to action. `brand.whatsappCta` already occupies that ground, and letting
 * the two blur would invite the yellow onto a contact button.
 *
 * Both fills are light enough to demand dark text (`BLUE_DARK`); white on either fails contrast.
 */
export const CART_YELLOW = "#ffd214";
export const BUY_ORANGE = "#ff9a1a";
export const IN_STOCK_GREEN = "#0e7035";
// The other two availability states (docs/ordering-flow/README.md §3.3). Three distinguishable
// hues rather than three shades of one: "bajo pedido" and "a cotizar" are different promises, and
// a shopper scanning a grid has to tell them apart at a glance, not by reading.
export const BACKORDER_BLUE = "#0b5f9e";
export const QUOTE_PURPLE = "#6c3fa0";

declare module "@mui/material/styles" {
  interface Palette {
    purchase: {
      cart: string;
      cartHover: string;
      buy: string;
      buyHover: string;
      inStock: string;
      backorder: string;
      quote: string;
    };
    brand: {
      whatsapp: string;
      whatsappHover: string;
      whatsappCta: string;
      whatsappCtaHover: string;
      facebook: string;
      instagram: string;
      youtube: string;
      location: string;
    };
  }
  interface PaletteOptions {
    purchase?: {
      cart?: string;
      cartHover?: string;
      buy?: string;
      buyHover?: string;
      inStock?: string;
      backorder?: string;
      quote?: string;
    };
    brand?: {
      whatsapp?: string;
      whatsappHover?: string;
      whatsappCta?: string;
      whatsappCtaHover?: string;
      facebook?: string;
      instagram?: string;
      youtube?: string;
      location?: string;
    };
  }
}

const headingFont = "Antonio, sans-serif";
const bodyFont = "Inter, sans-serif";

const theme = createTheme({
  palette: {
    primary: {
      main: BLUE,
    },
    secondary: {
      main: GREEN,
    },
    error: {
      main: RED,
    },
    background: {
      default: "#ffffff",
      paper: CREAM,
    },
    text: {
      primary: BLUE_DARK,
      secondary: TEXT_SECONDARY,
    },
    purchase: {
      cart: CART_YELLOW,
      buy: BUY_ORANGE,
      inStock: IN_STOCK_GREEN,
      backorder: BACKORDER_BLUE,
      quote: QUOTE_PURPLE,
      // Derived once here rather than per component, so every surface that uses these agrees on
      // what "pressed" looks like.
      cartHover: darken(CART_YELLOW, 0.12),
      buyHover: darken(BUY_ORANGE, 0.12),
    },
    brand: {
      whatsapp: "#25D366",
      whatsappHover: "#1EBE57",
      // A calmer green than the official `#25D366`, which reads as neon against the navy footer
      // bar. Kept separate rather than replacing `whatsapp` so the icon touchpoints — where the
      // brand colour sits on white and is doing recognition work — are unaffected.
      whatsappCta: "#3E8E5A",
      whatsappCtaHover: "#347A4C",
      facebook: "#1877F3",
      instagram: "#E1306C",
      youtube: "#FF0000",
      location: "#1976d2",
    },
  },
  typography: {
    fontFamily: bodyFont,
    fontSize: 14,
    h1: { fontFamily: headingFont },
    h2: { fontFamily: headingFont },
    h3: { fontFamily: headingFont },
    h4: { fontFamily: headingFont },
    h5: { fontFamily: headingFont },
    h6: { fontFamily: headingFont },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: bodyFont,
          overflowX: "hidden",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          // MUI fades placeholders to `opacity: 0.42` of the input's own colour, which turns
          // `BLUE_DARK` into roughly 2:1 against white — the same unreadable grey EB-554 is fixing
          // in `text.secondary`, arrived at by a different route. A placeholder is a hint, so it
          // still sits below the value it stands in for; it just has to be legible while doing it.
          //
          // Two rules because MUI writes two: a field whose label has not shrunk hides its
          // placeholder entirely and brings it back on focus, from a nested selector that outranks
          // anything flat written here.
          "&::placeholder": {
            opacity: PLACEHOLDER_OPACITY,
          },
          "label[data-shrink=false] + .MuiInputBase-formControl &:focus::placeholder": {
            opacity: PLACEHOLDER_OPACITY,
          },
        },
      },
    },
  },
});

export default theme;
